import React, { FunctionComponent, useEffect, useState } from "react";
import {
  Box,
  Card,
  DialogActions,
  DialogContent,
  ListItemIcon,
  MenuItem,
  Select,
  Stack,
  Toolbar,
  useTheme
} from "@mui/material";
import styles from "./styles.module.scss";
import { AlbumsFilter } from "./Component";
import { strings } from "../../../localization/LocalizedStrings";
import { FilterElement, FilterSortOption, FilterSortOrder } from "../../../models/FilterElement";
import { ContainedButton } from "../../ContainedButton";
import { RoutesBuilder } from "../../../models/RoutesBuilder";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../hooks/isMobile";
import { AlertDialog } from "../../AlertDialog";
import { useCurrentPublicCollection } from "../../../hooks/collections/useCollectionManager";
import { IIsNegatedAlbumsParameters } from "../../../hooks/albums";
import { useUser } from "../../../hooks/session";
import { PiperDialog } from "../../PiperDialog";
import { Icon, IconType } from "../../../icons";
import { attributeIconMap } from "../../../models/AttributeUtilities";
import { AttributeType } from "../../attributes/AttributeList";

export const AlbumsFilterDialog: FunctionComponent<IAlbumsFilterDialogProps> = ({
  isPublic,
  isOpen,
  setIsOpen,
  initialFilterElements,
  initialIsNegated,
  initialSortOption,
  initialSortOrder,
  onFilterApplied
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [currentUser] = useUser.useState();
  const [currentPublicCollection] = useCurrentPublicCollection.useState();
  const [filterElements, setFilterElements] = useState<FilterElement[]>(initialFilterElements);
  const [isNegated, setIsNegated] = useState<IIsNegatedAlbumsParameters>(initialIsNegated);
  const [sortOption, setSortOption] = useState(initialSortOption);
  const [sortOrder, setSortOrder] = useState(initialSortOrder);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const [loading] = useState(false);
  useEffect(() => {
    setFilterElements(initialFilterElements);
    setIsNegated(initialIsNegated);
    setSortOption(initialSortOption);
    setSortOrder(initialSortOrder);
  }, [isOpen, isPublic]);
  const didMakeChanges = () => {
    return (
      filterElements != initialFilterElements ||
      isNegated != initialIsNegated ||
      sortOption != initialSortOption ||
      sortOrder != initialSortOrder
    );
  };
  const handleClose = () => {
    if (didMakeChanges()) {
      setIsConfirmCloseAlertOpen(true);
      return;
    }
    setIsOpen(false);
  };
  return (
    <PiperDialog
      fullWidth
      maxWidth={"md"}
      open={isOpen}
      onClose={handleClose}
      PaperProps={{ style: { height: "100%" } }}
    >
      <div className={styles.container}>
        <Toolbar className={styles.toolbar} sx={{ marginBottom: "0 !important" }}>
          <h3 style={{ color: theme.palette.primary.main }}>{strings.option_filter_albums}</h3>
          <Box flexGrow={1} />
          <ToolbarButton
            href={`${window.location.protocol}//${
              window.location.host
            }${RoutesBuilder.documentation.home()}${RoutesBuilder.documentation.album.filterAlbums()}`}
            target={"_blank"}
            tooltip={strings.help}
            iconType={IconType.Help}
          />
          <ToolbarButton
            loading={loading}
            onClick={handleClose}
            tooltip={strings.close}
            iconType={IconType.Close}
          />
        </Toolbar>
        <Card className={styles.orderByCard}>
          <Stack direction={isMobile ? "column" : "row"} spacing={isMobile ? 1 : 2}>
            <Stack sx={{ width: isMobile ? "100%" : "50%" }}>
              <p className={styles.orderTitle}>{strings.order_by_title}</p>
              <Select
                value={sortOption}
                sx={{
                  height: "36px",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center"
                  }
                }}
                size={"small"}
                onChange={event => setSortOption(event.target.value as FilterSortOption)}
              >
                <MenuItem value={FilterSortOption.ARTIST} dense>
                  <ListItemIcon>
                    <Icon type={attributeIconMap[AttributeType.ARTISTS]} />
                  </ListItemIcon>
                  {strings.artist_title}
                </MenuItem>
                {(!isPublic || !currentPublicCollection?.bannedFields?.includes("label")) && (
                  <MenuItem value={FilterSortOption.LABEL} dense>
                    <ListItemIcon>
                      <Icon type={attributeIconMap[AttributeType.LABELS]} />
                    </ListItemIcon>
                    {strings.label_title}
                  </MenuItem>
                )}
                <MenuItem value={FilterSortOption.ORIGINAL_YEAR} dense>
                  <ListItemIcon>
                    <Icon type={IconType.Event} />
                  </ListItemIcon>
                  {strings.original_year_title}
                </MenuItem>
                <MenuItem value={FilterSortOption.RELEASE_YEAR} dense>
                  <ListItemIcon>
                    <Icon type={IconType.Event} />
                  </ListItemIcon>
                  {strings.release_year_title}
                </MenuItem>
                {(!isPublic || !currentPublicCollection?.bannedFields?.includes("store")) && (
                  <MenuItem value={FilterSortOption.STORE} dense>
                    <ListItemIcon>
                      <Icon type={attributeIconMap[AttributeType.STORES]} />
                    </ListItemIcon>
                    {strings.store_title}
                  </MenuItem>
                )}
                <MenuItem value={FilterSortOption.COUNTRY} dense>
                  <ListItemIcon>
                    <Icon type={attributeIconMap[AttributeType.ORIGIN_COUNTRIES]} />
                  </ListItemIcon>
                  {strings.origin_country_title}
                </MenuItem>
                {currentUser?.role != "guest" &&
                  (!isPublic ||
                    !currentPublicCollection?.bannedFields?.includes("purchasePrice")) && (
                    <MenuItem value={FilterSortOption.PURCHASE_PRICE} dense>
                      <ListItemIcon>
                        <Icon type={IconType.Paid} />
                      </ListItemIcon>
                      {strings.purchase_price_title}
                    </MenuItem>
                  )}
                {currentUser?.role != "guest" &&
                  (!isPublic || !currentPublicCollection?.bannedFields?.includes("sellValue")) && (
                    <MenuItem value={FilterSortOption.SELL_VALUE} dense>
                      <ListItemIcon>
                        <Icon type={IconType.FinanceChip} />
                      </ListItemIcon>
                      {strings.sort_by_sell_value}
                    </MenuItem>
                  )}
                {currentUser?.role != "guest" &&
                  (!isPublic || !currentPublicCollection?.bannedFields?.includes("sellValue")) && (
                    <MenuItem value={FilterSortOption.SELL_VALUE_DOLLARS} dense>
                      <ListItemIcon>
                        <Icon type={IconType.Paid} />
                      </ListItemIcon>
                      {strings.sort_by_sell_value_dollars}
                    </MenuItem>
                  )}
                {currentUser?.role != "guest" &&
                  (!isPublic || !currentPublicCollection?.bannedFields?.includes("revenue")) && (
                    <MenuItem value={FilterSortOption.REVENUE} dense>
                      <ListItemIcon>
                        <Icon type={IconType.Wallet} />
                      </ListItemIcon>
                      {strings.revenue_title}
                    </MenuItem>
                  )}
                {currentUser?.role != "guest" &&
                  (!isPublic ||
                    !currentPublicCollection?.bannedFields?.includes("purchaseDate")) && (
                    <MenuItem value={FilterSortOption.PURCHASE_DATE} dense>
                      <ListItemIcon>
                        <Icon type={IconType.CalendarMonth} />
                      </ListItemIcon>
                      {strings.purchase_date_title}
                    </MenuItem>
                  )}
                <MenuItem value={FilterSortOption.TRACK_COUNT} dense>
                  <ListItemIcon>
                    <Icon type={IconType.FormatListNumbered} />
                  </ListItemIcon>
                  {strings.track_count_title}
                </MenuItem>
                <MenuItem value={FilterSortOption.LENGTH} dense>
                  <ListItemIcon>
                    <Icon type={IconType.Timer} />
                  </ListItemIcon>
                  {strings.album_length_title}
                </MenuItem>
                <MenuItem value={FilterSortOption.RANDOM} dense>
                  <ListItemIcon>
                    <Icon type={IconType.Die} />
                  </ListItemIcon>
                  {strings.random_title}
                </MenuItem>
              </Select>
            </Stack>
            <Stack sx={{ width: isMobile ? "100%" : "50%" }}>
              <p className={styles.orderTitle}>{strings.order_direction_title}</p>
              <Select
                value={sortOrder}
                sx={{
                  height: "36px",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center"
                  }
                }}
                size={"small"}
                disabled={sortOption == FilterSortOption.RANDOM}
                onChange={event => setSortOrder(event.target.value as FilterSortOrder)}
              >
                <MenuItem value={FilterSortOrder.ASCENDANT} dense>
                  <ListItemIcon>
                    <Icon type={IconType.ArrowUpwardAlt} />
                  </ListItemIcon>
                  {strings.order_asc_title}
                </MenuItem>
                <MenuItem value={FilterSortOrder.DESCENDANT} dense>
                  <ListItemIcon>
                    <Icon type={IconType.ArrowDownwardAlt} />
                  </ListItemIcon>
                  {strings.order_desc_title}
                </MenuItem>
              </Select>
            </Stack>
          </Stack>
        </Card>
        <DialogContent>
          <AlbumsFilter
            isPublic={isPublic}
            initialFilterElements={initialFilterElements}
            initialIsNegated={initialIsNegated}
            onFilterChanged={(filterElements, isNegated) => {
              setFilterElements(filterElements);
              setIsNegated(isNegated);
            }}
          />
        </DialogContent>
        <DialogActions>
          <ContainedButton
            className={styles.applyButton}
            onClick={() => {
              setIsOpen(false);
              if (didMakeChanges()) {
                onFilterApplied(filterElements, isNegated, sortOption, sortOrder);
              }
            }}
          >
            {strings.filter_confirm}
          </ContainedButton>
        </DialogActions>
      </div>
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={() => setIsOpen(false)}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
    </PiperDialog>
  );
};

export interface IAlbumsFilterDialogProps {
  isPublic: boolean;
  isOpen: boolean;
  setIsOpen: (newValue: boolean) => void;
  initialFilterElements: FilterElement[];
  initialIsNegated: IIsNegatedAlbumsParameters;
  initialSortOrder: FilterSortOrder;
  initialSortOption: FilterSortOption;
  onFilterApplied: (
    filterElements: FilterElement[],
    isNegated: IIsNegatedAlbumsParameters,
    sortOption: FilterSortOption,
    sortOrder: FilterSortOrder
  ) => void;
}
